import PropTypes from 'prop-types';
import { useMemo } from "react";
import { useTranslation } from "react-i18next";
import Attachment from "../../common/components/Attachment";
import DataTable from "../../common/components/DataTable";
import { SettingsDisplay } from "../../common/components/SettingsPanels";
import useClientConfig from "../../common/hooks/clientConfigHook";
import useCredentials from "../../common/hooks/credentialsHook";
import useNetwork from "../../common/hooks/networkHook";
import { addRefDoc, canCreateRefDoc, canRemoveRefDoc, removeRefDoc, useRefDocs } from "../../reducers/refDocSlice";

const defaultSort = ['site'];

const ReferenceDocs = ({ uuid, label, onClose }) => {
    const { t } = useTranslation();
    const docs = useRefDocs();
    const network = useNetwork();
    const clientConfig = useClientConfig();
    const credentials = useCredentials();

    let displayDocs = useMemo(() => docs, [docs]);

    const columns = useMemo(() => {
        let columns = [{type: 'type', id: 'type', field: ['site','level','type'], wildcard: t('anyWildcard'), title: t('type', { count: 1 }), strict: false, label: t('type', { count: 1 }), editable: false}];
        if (clientConfig.enableLevels) {
            columns.unshift({type: 'level', id: 'level', field: ['site','level','type'], wildcard: t('anyWildcard'), title: t('level', { count: 1 }), strict: false, label: t('level', { count: 1 }), editable: false });
        }
        if (clientConfig.enableSites) {
            columns.unshift({type: 'site', id: 'site', field: ['site','level','type'], wildcard: t('anyWildcard'), title: t('site', { count: 1 }), strict: false, label: t('site', { count: 1 }), editable: false});
        }
        columns.push({type: 'dynamic', id: 'file', title: t('computerFile'), required: true, def: (value, disabled, hideLabel, onChange) => {
            if (value.id) {
                return <Attachment filename={value.filename} srcURL={'/refDoc/' + encodeURIComponent(value.id)}></Attachment>
            } else {
                return <Attachment filename={value.file?.name} onSelect={f => { 
                    onChange(f, 'file')
                }}></Attachment>
            }
        }})
        return columns;
    }, [t, clientConfig.enableSites, clientConfig.enableLevels])

    const handleAdd = newRow => {
        let data = new FormData();
        data.append('file', newRow.file);
        data.append('filename', newRow.file.name);
        if (newRow.site) {
            data.append('site', newRow.site);
        }
        if (newRow.level) {
            data.append('level', newRow.level);
        }
        if (newRow.type) {
            data.append('type', newRow.type);
        }
        addRefDoc(network, data);
    }

    const handleRemove = (doc) => {
        removeRefDoc(network, doc.id);
    }

    return <SettingsDisplay uuid={uuid} title={label} onClose={onClose}>
        <DataTable columns={columns} disableAdd={!canCreateRefDoc(credentials)} disableRemove={!canRemoveRefDoc(credentials)} defaultSort={defaultSort} value={displayDocs} onAdd={handleAdd} onRemove={handleRemove} showFilter={true}></DataTable>
    </SettingsDisplay>
}

ReferenceDocs.propTypes = {
    /**
     * ID to identify the settings status entry
     */
    uuid: PropTypes.string.isRequired,
    /**
     * Label for the screen
     */
    label: PropTypes.string.isRequired,
    /**
     * Function to call when the screen is closed
     */
    onClose: PropTypes.func
}

export default ReferenceDocs;
